/**
 * Shared iframe srcDoc builder for the frontend sandbox (main preview + library thumbnails).
 */

/** Keep bundled preview under ~1×750k code units so the tab stays responsive. */
export const SANDBOX_MAX_TOTAL_CHARS = 750_000

/**
 * Preview iframes may call `parent.postMessage(payload, '*')` with this envelope.
 * The main preview uses `sandbox="allow-scripts"` without `allow-same-origin` (opaque
 * origin); the parent must verify `event.source` is the preview frame and accept
 * `event.origin === 'null'`.
 */
export const SANDBOX_PREVIEW_POSTMESSAGE_SOURCE = 'nogrok:sandbox-preview' as const

export type SandboxPreviewHighlightLibraryTabMessage = {
  source: typeof SANDBOX_PREVIEW_POSTMESSAGE_SOURCE
  type: 'highlight-library-tab'
}

export function isSandboxPreviewHighlightLibraryTabMessage(
  data: unknown,
): data is SandboxPreviewHighlightLibraryTabMessage {
  if (typeof data !== 'object' || data === null) return false
  const o = data as Record<string, unknown>
  return o.source === SANDBOX_PREVIEW_POSTMESSAGE_SOURCE && o.type === 'highlight-library-tab'
}

/**
 * Preview document CSP: no network (connect-src none), no remote script/style URLs,
 * no nested frames/objects; user HTML/CSS and one inline script bundle only.
 */
export const PREVIEW_CSP =
  "default-src 'none'; base-uri 'none'; form-action 'none'; frame-ancestors 'none'; object-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; font-src data: blob:; connect-src 'none'; media-src 'none'; worker-src 'none'"

export type BuildPreviewSrcDocOptions = {
  theme?: 'light' | 'dark'
}

/**
 * CSS custom properties for preset HTML/CSS — mirror app light/dark when `buildPreviewSrcDoc` is called.
 * Presets should prefer these over hard-coded hex for surfaces, text, borders, and the primary accent.
 */
const PREVIEW_THEME_TOKENS_CSS = `
html {
  color-scheme: dark;
  --nogrok-preview-surface: #0c0e10;
  --nogrok-preview-on-surface: #e2e2e5;
  --nogrok-preview-on-surface-variant: #928b94;
  --nogrok-preview-on-surface-muted: #bfc3c9;
  --nogrok-preview-surface-container: #14181c;
  --nogrok-preview-surface-container-high: #2a3038;
  --nogrok-preview-surface-rail: #1a1f24;
  --nogrok-preview-surface-elevated: #1c2228;
  --nogrok-preview-outline: #3f4349;
  --nogrok-preview-primary: #ff6b00;
  --nogrok-preview-on-primary: #0c0e10;
  --nogrok-preview-accent-warm: #ffb366;
  --nogrok-preview-accent-deep: #c44e00;
  --nogrok-preview-selected-tint: #23180f;
  --nogrok-preview-shimmer-a: #1e242a;
  --nogrok-preview-shimmer-b: #2e3640;
  --nogrok-preview-shimmer-bg: #1a1510;
}
html.nogrok-preview-light {
  color-scheme: light;
  --nogrok-preview-surface: #fcf9f0;
  --nogrok-preview-on-surface: #1c1c17;
  --nogrok-preview-on-surface-variant: #6b5348;
  --nogrok-preview-on-surface-muted: #5a524c;
  --nogrok-preview-surface-container: #fffef8;
  --nogrok-preview-surface-container-high: #f1eee5;
  --nogrok-preview-surface-rail: #ebe6dc;
  --nogrok-preview-surface-elevated: #e4dfd4;
  --nogrok-preview-outline: #b8a99a;
  --nogrok-preview-primary: #ff6b00;
  --nogrok-preview-on-primary: #1c1c17;
  --nogrok-preview-accent-warm: #c25400;
  --nogrok-preview-accent-deep: #9a3d00;
  --nogrok-preview-selected-tint: #ffe8d4;
  --nogrok-preview-shimmer-a: #e8e4db;
  --nogrok-preview-shimmer-b: #f5f0e6;
  --nogrok-preview-shimmer-bg: #fff5eb;
}
`.trim()

/** Minimal light palette so preset thumbnails/main preview match app light theme (presets ship dark baseCardCss). */
const PREVIEW_LIGHT_SHIM_CSS = `
html.nogrok-preview-light body{background:#fcf9f0!important;color:#1c1c17!important}
html.nogrok-preview-light h1{color:#a04100!important}
html.nogrok-preview-light .card,html.nogrok-preview-light .panel{background:#fff!important;border-color:#e2bfb0!important;color:#1c1c17!important}
html.nogrok-preview-light label{color:#5a4136!important}
html.nogrok-preview-light input{background:#fff!important;border-color:#8e7164!important;color:#1c1c17!important}
html.nogrok-preview-light li:hover{background:#f1eee5!important}
html.nogrok-preview-light li.selected{border-color:#ff6b00!important;background:#ff6b0024!important}
html.nogrok-preview-light #sandbox-starter-cta,html.nogrok-preview-light #btn{color:#1c1c17!important;border-color:#ff6b00!important}
html.nogrok-preview-light #sandbox-starter-cta{background:#ff6b0024!important}
html.nogrok-preview-light #btn{background:#ff6b0022!important}
html.nogrok-preview-light #toggle{color:#1c1c17!important;border-color:#8e7164!important;background:#fff!important}
html.nogrok-preview-light #submit{background:#ff6b00!important;color:#fff!important}
html.nogrok-preview-light p#out{color:#1c1c17!important}
`.trim()

export function buildPreviewSrcDoc(
  htmlChunk: string,
  cssChunk: string,
  scriptBody: string,
  options?: BuildPreviewSrcDocOptions,
): string {
  const total = htmlChunk.length + cssChunk.length + scriptBody.length
  if (total > SANDBOX_MAX_TOTAL_CHARS) {
    const msg = `Preview source exceeds ${SANDBOX_MAX_TOTAL_CHARS.toLocaleString()} characters (HTML+CSS+JS total: ${total.toLocaleString()}). Reduce size to render.`
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${PREVIEW_CSP}"><title>Preview limit</title></head><body><pre style="margin:1rem;font-family:IBM Plex Mono,ui-monospace,monospace;font-size:13px;white-space:pre-wrap;">${msg.replace(/</g, '&lt;')}</pre></body></html>`
  }
  const htmlClass = options?.theme === 'light' ? ' class="nogrok-preview-light"' : ''
  const lightShim = options?.theme === 'light' ? `<style>${PREVIEW_LIGHT_SHIM_CSS}</style>` : ''
  return `<!DOCTYPE html><html${htmlClass}><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${PREVIEW_CSP}"><style>${PREVIEW_THEME_TOKENS_CSS}</style><style>${cssChunk}</style>${lightShim}</head><body>${htmlChunk}<script>${scriptBody}<\/script></body></html>`
}
